// Day Profile Chart with PV, Grid, and Shortfall visualization
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EnergyBalancePoint, EnergyBalanceSummary } from '@/lib/epseEnergyBalance';
import { Sun, Snowflake } from 'lucide-react';
import {
  ComposedChart,
  Area,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from 'recharts';

interface EPSEDayProfileChartProps {
  points: EnergyBalancePoint[];
  summary: EnergyBalanceSummary;
  connectionLimit: number;
  season: 'winter' | 'zomer';
  onSeasonChange: (season: 'winter' | 'zomer') => void;
  peakKw: number;
}

export function EPSEDayProfileChart({
  points,
  summary,
  connectionLimit,
  season,
  onSeasonChange,
  peakKw,
}: EPSEDayProfileChartProps) {
  // Format chart data - show hour number every 4 quarters
  const chartData = points.map((p, i) => ({
    ...p,
    displayTime: i % 4 === 0 ? p.hour.toString() : '', // Show just 0, 1, 2, ... 23
    // Create shortfall area (demand above connection limit)
    shortfall_display: p.demand_kw > connectionLimit ? p.demand_kw - connectionLimit : 0,
  }));

  const maxDemand = Math.max(...points.map(p => p.demand_kw));
  const yMax = Math.ceil(Math.max(maxDemand, connectionLimit * 1.2) / 10) * 10;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Dagprofiel met Energie Balans</CardTitle>
            <CardDescription>
              Vraag, PV-opbrengst, netinkoop en tekort visualisatie
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={season === 'winter' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onSeasonChange('winter')}
              className="flex items-center gap-1"
            >
              <Snowflake className="h-4 w-4" />
              Winter
            </Button>
            <Button
              variant={season === 'zomer' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onSeasonChange('zomer')}
              className="flex items-center gap-1"
            >
              <Sun className="h-4 w-4" />
              Zomer
            </Button>
          </div>
        </div>
        
        {/* Summary badges */}
        <div className="flex flex-wrap gap-2 mt-3">
          <Badge variant="outline">
            Piekvermogen: {peakKw.toFixed(0)} kW
          </Badge>
          <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30">
            Tekort: {summary.shortfall_kwh.toFixed(0)} kWh/dag
          </Badge>
          <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">
            {summary.hours_above_limit.toFixed(1)} uur boven limiet
          </Badge>
          {summary.pv_generated_kwh > 0 && (
            <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-500/30">
              PV: {summary.pv_used_kwh.toFixed(0)} kWh gebruikt
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="displayTime" 
                tick={{ fontSize: 10 }}
                interval={0}
              />
              <YAxis 
                domain={[0, yMax]}
                tick={{ fontSize: 10 }}
                label={{ value: 'kW', angle: -90, position: 'insideLeft', fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                }}
                formatter={(value: number, name: string) => {
                  const labels: Record<string, string> = {
                    demand_kw: 'Vraag',
                    grid_actual_kw: 'Net Inkoop',
                    pv_used_kw: 'PV Gebruikt',
                    shortfall_display: 'Tekort',
                    battery_discharge_kw: 'Uit Batterij',
                  };
                  return [`${value.toFixed(1)} kW`, labels[name] || name];
                }}
              />
              <Legend 
                formatter={(value) => {
                  const labels: Record<string, string> = {
                    demand_kw: 'Vraag',
                    grid_actual_kw: 'Net Inkoop',
                    pv_used_kw: 'PV Opbrengst',
                    shortfall_display: 'Tekort (boven limiet)',
                    battery_discharge_kw: 'Batterij Ontlading',
                  };
                  return labels[value] || value;
                }}
              />

              {/* Connection limit line */}
              <ReferenceLine
                y={connectionLimit}
                stroke="hsl(var(--destructive))"
                strokeWidth={2}
                strokeDasharray="8 4"
                label={{
                  value: `Limiet: ${connectionLimit} kW`,
                  position: 'right',
                  fill: 'hsl(var(--destructive))',
                  fontSize: 11,
                  fontWeight: 'bold',
                }}
              />

              {/* Demand area (blue, base) */}
              <Area
                type="stepAfter"
                dataKey="demand_kw"
                fill="hsl(var(--primary) / 0.2)"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                name="demand_kw"
              />

              {/* Shortfall area (red, above limit) */}
              <Area
                type="stepAfter"
                dataKey="shortfall_display"
                fill="hsl(var(--destructive) / 0.6)"
                stroke="hsl(var(--destructive))"
                strokeWidth={0}
                name="shortfall_display"
                baseValue={connectionLimit}
              />

              {/* PV contribution (green area) */}
              <Area
                type="monotone"
                dataKey="pv_used_kw"
                fill="hsl(142 76% 36% / 0.4)"
                stroke="hsl(142 76% 36%)"
                strokeWidth={1.5}
                name="pv_used_kw"
              />

              {/* Grid actual (blue line) */}
              <Line
                type="stepAfter"
                dataKey="grid_actual_kw"
                stroke="hsl(var(--chart-2))"
                strokeWidth={2}
                dot={false}
                name="grid_actual_kw"
              />

              {/* Battery discharge (bars) */}
              <Bar
                dataKey="battery_discharge_kw"
                fill="hsl(var(--chart-3))"
                opacity={0.8}
                name="battery_discharge_kw"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
